// In-app device portal.
//
// Fetches the device list (RustDesk ID + password per machine) from the
// dashboard's backend API. The client and the API are served from the SAME
// origin (e.g. both on rustdesk.spencerschools.org), so the browser sends the
// user's existing dashboard session cookie automatically on the fetch — no CORS,
// no bearer token, no third-party-cookie issues. If the user isn't logged in the
// API returns 401/403 and we surface a sign-in link.
//
// Credentials returned by the API are held in memory only and never persisted.

export interface PortalDevice {
  id: string; // RustDesk ID (required)
  name?: string; // display name
  password?: string; // RustDesk permanent password (used once to log in)
  group?: string; // optional grouping/section
  online?: boolean; // optional presence indicator
  os?: string; // optional, informational
  // Optional per-device server overrides; omit to use the client's baked/saved server.
  server?: string;
  relay?: string;
  key?: string;
  schema?: "wss://" | "ws://";
}

interface PortalConfig {
  enabled?: boolean;
  devicesUrl?: string; // default "/api/devices" (same-origin)
  loginUrl?: string; // where to send unauthenticated users (default: none)
}

function cfg(): PortalConfig {
  const w = (window as unknown as { RUSTDESK_PORTAL?: PortalConfig }).RUSTDESK_PORTAL;
  return w && typeof w === "object" ? w : {};
}

/** Portal is on if explicitly enabled, or if a devicesUrl was configured. */
export function isPortalEnabled(): boolean {
  const c = cfg();
  return c.enabled === true || (c.enabled !== false && c.devicesUrl != null);
}

export function portalLoginUrl(): string | undefined {
  return cfg().loginUrl || undefined;
}

/** Thrown when the API says the user isn't authenticated (401/403). */
export class PortalAuthError extends Error {}

/**
 * GET the device list from the backend using the same-origin session cookie.
 * Accepts either a bare array or `{devices:[…]}` / `{peers:[…]}`, and maps a
 * range of common field names so it adapts to an existing API shape.
 */
export async function fetchDevices(): Promise<PortalDevice[]> {
  const url = cfg().devicesUrl || "/api/devices";
  const res = await fetch(url, {
    method: "GET",
    credentials: "include", // send the dashboard session cookie (same-origin)
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (res.status === 401 || res.status === 403) throw new PortalAuthError("Not signed in");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data: unknown = await res.json();
  const rawList = Array.isArray(data)
    ? data
    : ((data as { devices?: unknown; peers?: unknown })?.devices ??
       (data as { peers?: unknown })?.peers ??
       []);
  if (!Array.isArray(rawList)) throw new Error("Unexpected device-list shape");

  return rawList.map(normalize).filter((d) => d.id.length > 0);
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length ? v : undefined;
}

function normalize(raw: Record<string, unknown>): PortalDevice {
  const r = raw ?? {};
  const schema = r.schema === "ws://" ? "ws://" : r.schema === "wss://" ? "wss://" : undefined;
  return {
    id: (str(r.id) ?? str(r.rustdesk_id) ?? str(r.peer_id) ?? "").replace(/\s/g, ""),
    name: str(r.name) ?? str(r.hostname) ?? str(r.alias) ?? str(r.label),
    password: str(r.password) ?? str(r.pw) ?? str(r.pass),
    group: str(r.group) ?? str(r.tag) ?? str(r.section),
    online: typeof r.online === "boolean" ? r.online : undefined,
    os: str(r.os) ?? str(r.platform),
    server: str(r.server),
    relay: str(r.relay),
    key: str(r.key),
    schema,
  };
}
